import PQueue from 'p-queue';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GeodataMarker } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { PutMarkerParameters } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';

type UnpinMarkersCallbackParams = {
  geodataId: string;
  markers: GeodataMarker[];
  updateMarker: (marker: GeodataMarker) => void;
};

/**
 * Returns a callback that unpins multiple markers using a pQueue.
 * The callback takes the current selection of pinned markers, creates a pQueue,
 * and for each marker it patches the marker to set pinned: false.
 */
export function useUnpinMarkersCallback() {
  const [isPending, setIsPending] = useState(false);
  const { t } = useTranslation();
  const pQueue = useRef(
    new PQueue({
      concurrency: 3,
      autoStart: true,
    }),
  );

  const unpinMarkers = useCallback(
    async ({ geodataId, markers, updateMarker }: UnpinMarkersCallbackParams) => {
      const pinnedMarkers = markers.filter((marker) => marker.pinned);
      let hasFailure = false;

      setIsPending(true);

      for (const marker of pinnedMarkers) {
        if (!marker.gcpId || !marker.imageId) {
          continue;
        }

        void pQueue.current.add(async () => {
          try {
            const params: PutMarkerParameters = {
              path: {
                geodataId,
                markerId: marker.id,
              },
              body: {
                gcpId: marker.gcpId,
                imageId: marker.imageId,
                pinned: false,
                position: marker.position,
              },
            };

            const result = await SkyMapAxiosServiceFactory.instance
              .createGeodataServiceV0()
              .putMarker(params);
            updateMarker(result.data);
          } catch (error) {
            hasFailure = true;
          }
        });
      }

      await pQueue.current.onIdle();
      setIsPending(false);

      if (hasFailure) {
        publish(SubscriptionTopic.ToastrMessage, {
          type: 'error',
          message: t('reviewGcpMarkerEstimatesDialog.someMarkersFailedToUpdate', {
            ns: 'cloudProcessing',
          }),
        });
      }
    },
    [t],
  );

  // Ensure the queue is cleared when the component unmounts
  useEffect(() => {
    const queue = pQueue.current;
    return () => queue.clear();
  }, []);

  return { unpinMarkers, isPending };
}
